import { useState } from 'react';
import { parseColor, serializeColor, toHex6, setRgb, setAlpha, type Rgba } from './colorValue';

export interface ColorFieldProps {
  label: string;
  /** Effective value (override if set, else the knob's resolved default). */
  resolved: string;
  /** The raw explicit override ('' when unset) — drives the controlled text. */
  override: string;
  onChange: (next: string | number | undefined) => void;
  hasOverride: boolean;
}

const FALLBACK: Rgba = { r: 0, g: 0, b: 0, a: 1 };

/**
 * Alpha-capable colour control. Three synced affordances:
 *   • RGB swatch (`<input type=color>`) — sets channels, PRESERVES alpha.
 *   • alpha slider (0-100%) — sets opacity only.
 *   • text field (authoritative) — accepts ANY css colour (hex 3/4/6/8,
 *     rgb(a), hsl(a), transparent, and pass-through for currentColor/var()).
 *
 * Editor-only: the stored value is still a raw colour string. When the text
 * isn't parseable to RGBA (currentColor, var(--x), unknown name) the swatch +
 * slider disable and the text is preserved verbatim — nothing is rewritten.
 *
 * Replaces the old hex-only ColorRow, which silently dropped rgba()/alpha so
 * partners couldn't set translucent modal overlays.
 */
export function ColorField({ label, resolved, override, onChange, hasOverride }: ColorFieldProps) {
  // Local text mirrors the input so it stays controlled and reflects resets —
  // same controlled-reset pattern the old ColorRow used.
  const [text, setText] = useState(override);
  if (text !== override) setText(override);

  // Swatch + slider read the effective colour (override, else default).
  const rgba = parseColor(text || resolved);
  const parseable = rgba !== null;
  const base = rgba ?? FALLBACK;

  // Text is "valid" (no red border) when empty or parseable. currentColor /
  // var() stay typeable but show the warning border (can't be committed by
  // the picker, only carried through as-is on the rare hand-typed value).
  const valid = text === '' || parseColor(text) !== null;
  const swatchHex = rgba ? toHex6(rgba) : '#000000';
  const alphaPct = rgba ? Math.round(rgba.a * 100) : 100;

  const commit = (next: string) => {
    setText(next);
    onChange(next);
  };

  // Two-line layout: label (+reset) on top, swatch + alpha slider + free-text
  // value full-width below. Keeps everything readable in the 360px sidebar —
  // a single row with label + swatch + slider + text + reset truncated the
  // labels and clipped the reset button once the alpha slider was added.
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span
          title={label}
          className="truncate font-mono text-[10px] uppercase tracking-wide text-neutral-400"
        >
          {label}
        </span>
        {hasOverride && (
          <button
            type="button"
            aria-label={`Reset ${label}`}
            onClick={() => onChange(undefined)}
            className="shrink-0 rounded border border-neutral-700 px-2 py-0.5 text-[10px] text-neutral-400 hover:text-white"
          >
            reset
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          aria-label={`${label} color picker`}
          type="color"
          disabled={!parseable}
          className="h-7 w-7 shrink-0 cursor-pointer rounded border border-neutral-700 bg-transparent p-0 disabled:opacity-40"
          value={swatchHex}
          onChange={(e) => commit(serializeColor(setRgb(base, e.target.value)))}
        />
        <input
          aria-label={`${label} alpha`}
          title="Opacity %"
          type="range"
          min={0}
          max={100}
          step={1}
          disabled={!parseable}
          className="h-1 w-16 shrink-0 cursor-pointer accent-neutral-300 disabled:opacity-40"
          value={alphaPct}
          onChange={(e) => commit(serializeColor(setAlpha(base, Number(e.target.value) / 100)))}
        />
        <input
          aria-label={label}
          type="text"
          className={`min-w-0 flex-1 rounded border bg-neutral-900 px-2 py-1 font-mono text-[12px] text-white outline-none ${valid ? 'border-neutral-700' : 'border-red-500'}`}
          placeholder={resolved}
          value={text}
          onChange={(e) => {
            const next = e.target.value;
            setText(next);
            if (next === '') onChange(undefined);
            else if (parseColor(next) !== null) onChange(next);
          }}
        />
      </div>
    </div>
  );
}
